import React, { useMemo } from "react";
import { Area, Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { brand, chartColors, neutral } from "../../theme/palette";
import { formatINR } from "../../utils/formatINR";
import { formatCompactINR, refundTrend, sessionTrend } from "../../utils/analyticsMetrics";
import { ChartCard, ChartTooltip, Legend, SegmentedControl, axisProps } from "./AnalyticsUI";

export const GRANULARITY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const RevenueChart = ({ sessions, transactions, showRefunds, range, granularity, onGranularityChange, isLoading, error, refundsLoading, refundsError }) => {
  const data = useMemo(() => {
    const trend = sessionTrend(sessions, range.fromDate, range.toDate, granularity);
    if (!showRefunds || !transactions) return trend;
    const refunds = refundTrend(transactions, range.fromDate, range.toDate, granularity);
    return trend.map((t) => ({ ...t, refunds: refunds.get(t.bucket) || 0 }));
  }, [sessions, transactions, showRefunds, range.fromDate, range.toDate, granularity]);

  const totals = useMemo(
    () => data.reduce((a, d) => ({ revenue: a.revenue + d.revenue, refunds: a.refunds + (d.refunds || 0) }), { revenue: 0, refunds: 0 }),
    [data]
  );

  const refundsReady = showRefunds && !refundsLoading && !refundsError;

  return (
    <ChartCard
      title="Revenue over time"
      description="Charging revenue billed per period (session final cost, incl. GST)."
      actions={<SegmentedControl options={GRANULARITY_OPTIONS} value={granularity} onChange={onGranularityChange} />}
      isLoading={isLoading}
      error={error}
      isEmpty={!sessions?.length}
      height={300}
    >
      <div className="mb-4 flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <p className="text-xs font-medium text-muted">Charging revenue</p>
          <p className="text-xl font-semibold tabular-nums text-fg">{formatINR(totals.revenue)}</p>
        </div>
        {showRefunds && (
          <div>
            <p className="text-xs font-medium text-muted">Refunds issued</p>
            <p className="text-xl font-semibold tabular-nums text-red-700">
              {refundsLoading ? "…" : refundsError ? "—" : formatINR(totals.refunds)}
            </p>
          </div>
        )}
        <div className="ml-auto">
          <Legend
            items={[
              { label: "Charging revenue", color: brand[600] },
              ...(refundsReady ? [{ label: "Refunds", color: chartColors.danger }] : []),
            ]}
          />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={brand[600]} stopOpacity={0.18} />
              <stop offset="100%" stopColor={brand[600]} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={neutral[200]} strokeDasharray="3 3" />
          <XAxis dataKey="label" {...axisProps} minTickGap={24} />
          <YAxis {...axisProps} width={56} tickFormatter={formatCompactINR} />
          <Tooltip
            cursor={{ stroke: neutral[300], strokeDasharray: "3 3" }}
            content={<ChartTooltip formatters={{ revenue: formatINR, refunds: formatINR }} />}
          />
          {refundsReady && <Bar dataKey="refunds" name="Refunds" fill={chartColors.danger} fillOpacity={0.75} radius={[3, 3, 0, 0]} maxBarSize={18} />}
          <Area type="monotone" dataKey="revenue" name="Charging revenue" stroke={brand[600]} strokeWidth={2} fill="url(#revenueFill)" activeDot={{ r: 4, strokeWidth: 0 }} />
        </ComposedChart>
      </ResponsiveContainer>
      <p className="mt-3 text-xs text-muted">
        {showRefunds
          ? "Refunds are settled refund transactions and are not subtracted from revenue: a net revenue figure needs an agreed accounting rule (which refunds relate to charging)."
          : "Refund data is only available to platform administrators."}
      </p>
    </ChartCard>
  );
};

export default RevenueChart;
